import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { MessageSquare, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsManagementIssues({ user }) {
  const { toast } = useToast();
  const [issues, setIssues] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [issuesRes, perfRes] = await Promise.all([
        fetch(`${API_URL}/api/ops-extended/management-issues?ops_manager_id=${user.user_id}`, { credentials: "include" }),
        fetch(`${API_URL}/api/ops-extended/performance/${user.user_id}`, { credentials: "include" })
      ]);
      
      if (issuesRes.ok) setIssues(await issuesRes.json());
      if (perfRes.ok) setPerformance(await perfRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedIssue || !resolveNotes.trim()) {
      toast({ title: "Error", description: "Please provide resolution notes", variant: "destructive" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/management-issues/${selectedIssue.issue_id}/resolve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ resolution_notes: resolveNotes })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast({ 
          title: "Issue resolved successfully", 
          description: `Resolution time: ${Math.round(data.resolution_time_minutes)} minutes` 
        });
        setDialogOpen(false);
        setSelectedIssue(null);
        setResolveNotes("");
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to resolve issue", variant: "destructive" });
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-gray-500",
      medium: "bg-yellow-500",
      high: "bg-orange-500",
      urgent: "bg-red-500"
    };
    return colors[priority] || "bg-gray-500";
  };

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  const openIssues = issues.filter(i => i.status === "open");
  const resolvedIssues = issues.filter(i => i.status === "resolved");

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="ops-management-issues">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Issues</p>
                <p className="text-2xl font-bold text-yellow-600">{openIssues.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{resolvedIssues.length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Resolution</p>
                <p className="text-2xl font-bold">{performance?.average_resolution_time_minutes || 0} min</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600">
                  {openIssues.filter(i => isOverdue(i.resolution_deadline)).length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Open Issues */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Issues from Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          {openIssues.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>No open issues! You're all caught up.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {openIssues.map((issue) => (
                <div 
                  key={issue.issue_id} 
                  className={`border rounded-lg p-4 ${isOverdue(issue.resolution_deadline) ? "border-red-300 bg-red-50" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getPriorityColor(issue.priority)}>{issue.priority}</Badge>
                        {isOverdue(issue.resolution_deadline) && (
                          <Badge variant="destructive">OVERDUE</Badge>
                        )}
                      </div>
                      <h4 className="font-semibold">{issue.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{issue.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Deadline: {new Date(issue.resolution_deadline).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <Button 
                      onClick={() => { setSelectedIssue(issue); setDialogOpen(true); }}
                      variant="default"
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolved Issues History */}
      {resolvedIssues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resolution History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Resolved</TableHead>
                  <TableHead>Resolution Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resolvedIssues.map((issue) => (
                  <TableRow key={issue.issue_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{issue.title}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-xs">{issue.resolution_notes}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPriorityColor(issue.priority)}>{issue.priority}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(issue.resolved_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{Math.round(issue.resolution_time_minutes)} min</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Resolve Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Issue</DialogTitle>
          </DialogHeader>
          {selectedIssue && (
            <div className="space-y-4">
              <div className="bg-muted p-3 rounded-lg">
                <h4 className="font-medium">{selectedIssue.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{selectedIssue.description}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Resolution Notes *</label>
                <Textarea 
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Describe how you resolved this issue..."
                  rows={4}
                  className="mt-2"
                />
              </div>
              <Button onClick={handleResolve} className="w-full">Mark as Resolved</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
